import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import type { BingoLogSummary, BingoLogView } from '../../../shared/bingo.js';
import { saveBingoLogPdf } from './bingoLogPdf.js';

// The bingo logs (user request 2026-10-02): one per game – draws, bingos (also the ones nobody called),
// every Bingo! press with its result and the winners, with coupon pictures – as a PDF. For the admin and
// the bingo hosts; kept until removed.

type Flash = { ok: boolean; text: string } | null;
const l = () => t.admin.bingoLogs;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const when = (ms: number) => new Date(ms).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

export function BingoLogs(props: { onFlash: (f: Flash) => void }) {
  const [logs, setLogs] = useState<BingoLogSummary[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [removing, setRemoving] = useState<BingoLogSummary | null>(null);
  useEffect(() => {
    api<{ logs: BingoLogSummary[] }>('GET', '/api/admin/bingo/logs')
      .then((r) => setLogs(r.logs))
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  }, []);
  const download = async (x: BingoLogSummary) => {
    setBusy(x.gameId);
    try {
      await saveBingoLogPdf(await api<BingoLogView>('GET', `/api/admin/bingo/logs/${x.gameId}`));
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(null);
    }
  };
  const remove = async (x: BingoLogSummary) => {
    setRemoving(null);
    try {
      setLogs((await api<{ logs: BingoLogSummary[] }>('DELETE', `/api/admin/bingo/logs/${x.gameId}`)).logs);
      props.onFlash({ ok: true, text: l().removed(x.gameId) });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  return (
    <section class="card form">
      <h2>{l().title}</h2>
      <p class="hint">{l().info}</p>
      {!logs ? (
        <p class="muted">{t.admin.autoBingo.loading}</p>
      ) : logs.length === 0 ? (
        <p class="muted">{l().none}</p>
      ) : (
        <ul class="user-list">
          {logs.map((x) => (
            <li class="user-row" key={x.gameId}>
              <div class="user-main">
                <div class="user-name">
                  {l().game(x.gameId)} · {when(x.startedAt)} {x.endedAt === null && <span class="badge batch-active">{l().running}</span>}
                </div>
                <div class="user-sub">
                  {x.mode === 'auto' ? l().auto : l().host} · {x.labels} · {l().counts(x.coupons, x.players, x.draws, x.wins)}
                </div>
              </div>
              <div class="user-actions">
                <button class="btn btn-small btn-primary" type="button" disabled={busy !== null} onClick={() => void download(x)}>
                  {busy === x.gameId ? l().making : `⬇ ${l().pdf}`}
                </button>
                <button class="btn btn-small btn-danger" type="button" onClick={() => setRemoving(x)}>
                  {t.admin.bingo.remove}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {removing && (
        <ConfirmDialog
          title={l().removeTitle(removing.gameId)}
          confirmLabel={t.admin.bingo.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>{l().removeText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
